// Copyright (c) 2020 WiseTime. All rights reserved.

import { concat, Observable, of } from "rxjs"
import { Action } from "redux"
import { catchError, filter, map, mergeMap } from "rxjs/operators"
import * as TimelineActions from "../actions"
import * as TimelineSlice from "../slices/timeline"
import * as TimelineAdditionalUiSlice from "../slices/additionalUi"
import { AddToTimeGroupRequest, TimeGroup } from "../../../generated/wisetime/user/timeline/v4/timeline_pb"
import { timelineClient } from "../../../app/rpc/client"
import { grpc } from "../../../app/rpc/executor"
import { StatusCode } from "grpc-web"
import { isGrpcError } from "../../../lib/rpcutils"
import { t } from "@lingui/macro"
import { showSnackBar } from "../../snackbar/actions"

type AddToTimeGroupRpc = (request: AddToTimeGroupRequest) => Observable<TimeGroup>

export const addToTimeGroup = (addToTimeGroupRpc: AddToTimeGroupRpc) =>
  (action$: Observable<Action>): Observable<Action> =>
    action$.pipe(
      filter(TimelineActions.addToTimeGroup.match),
      mergeMap(action => of(action.payload)
        .pipe(
          mergeMap(addToTimeGroupRpc),
          map(timeGroup => timeGroup.toObject()),
          mergeMap(timeGroup =>
            concat(
              of(TimelineSlice.upsertTimeGroup(timeGroup)),
              of(TimelineActions.generateTimesheetFieldConfigs([timeGroup])),
              of(TimelineAdditionalUiSlice.addBlinkTimeRowsColumnTimeGroupIds(timeGroup.id)),
              of(TimelineSlice.removeActiveTimeRowIds({ timeRowIds: action.payload.getTimeRowIdsList() })),
            )
          ),
          catchError(handleError),
        )
      ),
    )

const addToTimeGroupRpc = (request: AddToTimeGroupRequest): Observable<TimeGroup> =>
  grpc(() => timelineClient.addToTimeGroup(request, null))

const handleError = (e: unknown) => {
  if (isGrpcError(e)) {
    const { code } = e
    if (code === StatusCode.NOT_FOUND) {
      return concat(
        of(showSnackBar({
          message: t({ id: "msg_add_to_group_not_found", message: "Time group or time row(s) not found." }),
          messageType: "ERROR",
        })),
        of(TimelineActions.onTimelineOutOfSync()),
      )
    }
    if (code === StatusCode.PERMISSION_DENIED) {
      return of(showSnackBar({
        message: t({ id: "msg_add_to_group_permission_denied", message: "Failed to add time row(s) to the group. You do not have permission to update the time group." }),
        messageType: "ERROR",
      }))
    }
    if (code === StatusCode.FAILED_PRECONDITION) {
      return concat(
        of(showSnackBar({
          message: t({ id: "msg_add_to_group_failed_precondition", message: "Time group has already been posted or time row(s) have already been attached to the group." }),
          messageType: "ERROR",
        })),
        of(TimelineActions.onTimelineOutOfSync()),
      )
    }
  }
  return of(showSnackBar({
    message: t({ id: "msg_add_to_group_failed", message: "Failed to add time row(s) to the group. Please try again in a couple of minutes. If the error persists, please contact support@wisetime.com." }),
    messageType: "ERROR",
  }))
}

export default addToTimeGroup(addToTimeGroupRpc)
